/* eslint-disable class-methods-use-this */
import { AxiosResponse } from 'axios';
import { http } from 'SharedDir/backend/axios/http';

import SimpleApi from 'SharedDir/backend/api/classes/simpleApi';
import { TTP_PERSON_API } from 'SharedDir/backend/endpoints/physical-person';
import { TTP, ttpRegistriesUrl } from 'SharedDir/backend/endpoints/ttp-registries';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';
import { ReportTaskBare } from 'SharedDir/declarations/report-tasks';
import { QuasarTableExportColumns } from 'SharedDir/declarations/shared';
import { TTPRegistries } from 'SharedDir/declarations/ttp-registries';

export interface ReportForm {
  date: string | null;
  nationsIds: number[];
  municipalityIds: number[];
  onlyDelegate: boolean;
  filename: string;
}

export interface ReportFormByUsers extends ReportForm {
  fromAge: number | null;
  toAge: number | null;
  activitiesIds: number[];
  birtYear: number | null;
  deerCount: number | null;
  deerComparison: string | null;
  foundationIncludedType: number | null;
  foundationExcludedType: number | null;
  sex: string | null;
  considerCommunes: boolean | null;
}

export interface ExcludeIncludeReportForm {
  dateStart: string | null;
  dateEnd: string | null;
  filename: string;
}

export interface TtpReportForm {
  date: number | null;
  nationsIds: number[];
  municipalityIds: number[];
  replaceNull: boolean;
  filename: string;
}

export interface StatisticReportForm {
  year: number | null;
  filename: string;
  title: string;
}

export interface AgeCategoriesReport extends StatisticReportForm {
  oldForm: boolean;
}

export interface DemographyReportForm {
  fromDate: null | number;
  toDate: null | number;
  isBirth: boolean;
  filename: string;
  title: string;
}

export interface AnswerPnpReportForm {
  // Для отчета МЧС (structure-ttp)
  ttpIds?: number[] | null;
  // Для отчета по реестру ТХД (structure-thd)
  municipalityIds?: number[] | null;
  localitiesIds?: number[] | null;
  licenseAreaIds?: number[] | null;

  addExcludedDelegate: boolean;
  addExcluded: boolean;
  // structure-thd: пустой список ЛУ + true → SQL IN () на бэке, поэтому фронт шлёт true только с выбранными ЛУ
  filteringBuilding?: boolean;
}

export interface RegistryReportForm {
  districtTtp: number[];
  addExcludedDelegate?: boolean | null;
  typesActivities: number[];
  nationality: number[];
  typeReport: 'Краткий' | 'Полный';
  filename: string;
}

export interface OrganizationsReportForm {
  filename: string;
  showExcluded: boolean;
  municipalityIds: number[];
}

export type AnswerEmercomReportForm = AnswerPnpReportForm;

export default class TtpApiClass extends SimpleApi<TTPRegistries.TTP, TTPRegistries.TTPCreate> {
  constructor() {
    super(`${ttpRegistriesUrl}/ttp`);
  }

  async showInfo(id: number): Promise<TTPRegistries.TTPInfo> {
    const { data } = await http({
      ...TTP.INFO(id),
    });

    return data;
  }

  /**
   * Генерация следующих отчета:
   *  "Итоговые показатели субъектов права по реестру ТТП",
   *  "Итоговые показатели субъектов права по реестру ТТП (все ФЛ)"
   * @param params
   * @returns
   */
  async generateFinalIndicatorsReport(params: ReportForm | ReportFormByUsers): Promise<any> {
    const response = await http({
      ...TTP.FINAL_INDICATORS_REPORT,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация следующего отчета:
   *  "Ответ МЧС (общины)",
   */
  async generateAnswerEmercomCommuneReport(params: Record<string, string>) {
    const response = await http({
      ...TTP.STRUCTURE_TTP_COMMUNES,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация следующего отчета:
   *  "Информация по реестру организаций, осуществляющих традиционную хозяйственную деятельность КМНС
   *  в ХМАО-Югре по основным видам деятельности,
   */
  async generateOrganizationActivitiesReport(params: Record<string, string>) {
    const response = await http({
      ...TTP.ORGANIZATION_ACTIVITIES,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация следующего отчета:
   *  "Показатели субъектов права по численности поголовья оленей"
   */
  async generateCountDeerReport(params: Record<string, string>) {
    const response = await http({
      ...TTP.COUNT_DEER,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчёта «Поголовье оленей» (потоковая выгрузка, deer-herd-fast).
   */
  async generateDeerHerdReport(params: Record<string, unknown>) {
    const response = (await http({
      ...TTP.DEER_HERD_FAST,
      params,
    })) as AxiosResponse<ReportTaskBare>;

    return response;
  }

  /**
   * @deprecated Используйте generateDeerHerdReport (тот же fast-endpoint).
   */
  async generateDeerHerdReportFast(params: Record<string, unknown>) {
    return this.generateDeerHerdReport(params);
  }

  /**
   * Генерация следующего отчета:
   *  "Отчёт о забое оленей"
   */
  async generateDeerSlaughterReport(params: Record<string, unknown>) {
    const response = await http({
      ...TTP.DEER_SLAUGHTER,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  "Итоговые показатели субъектов права по реестру ТТП по видам деятельности"
   * @param params
   * @returns
   */
  async generateActivitiesTypesReport(params: TtpReportForm): Promise<any> {
    const response = await http({
      ...TTP.ACTIVITIES_TYPES_REPORT,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  "Включение исключение организаций"
   * @param params
   * @returns
   */
  async generateOrganizationIncludeExcludeReport(params: ExcludeIncludeReportForm): Promise<any> {
    const response = await http({
      ...TTP.ORGANIZATION_INCLUDE_EXCLUDE_REPORT,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  Статистические данные Департамента недропользования и природных ресурсов
   *  Ханты-Мансийского автономного округа – Югры таблица на каждый год
   *
   * Часть отчета:
   *  Распределение численности субъектов права по возрастному признаку
   *  в Реестре территорий традиционного природопользования коренных малочисленных народов Севера
   * @param params
   * @returns
   */
  async generateStatisticDataReport(params: StatisticReportForm): Promise<any> {
    const response = await http({
      ...TTP.STATISTIC_DATA_REPORT,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  Статистические данные Департамента недропользования и природных ресурсов
   *  Ханты-Мансийского автономного округа – Югры таблица на каждый год
   *
   * Часть отчета:
   *  Информация из Реестра территорий традиционного природопользования
   *  коренных малочисленных народов Севера регионального значения в ХМАО
   * @param params
   * @returns
   */
  async generateAgeCategoriesReport(params: AgeCategoriesReport): Promise<any> {
    const response = await http({
      ...TTP.AGE_CATEGORIES_REPORT,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  Статистические данные Департамента недропользования и природных ресурсов
   *  Ханты-Мансийского автономного округа – Югры таблица на каждый год
   *
   * Часть отчета:
   *  Информация по Реестру территорий традиционного природопользования коренных малочисленных народов
   *  Севера регионального значения в ХМАО
   *  по количеству родившихся (включенных) / умерших (исключенных)
   * @returns
   */
  async generateDemographyReport(params: DemographyReportForm): Promise<any> {
    const response = await http({
      ...TTP.DEMOGRAPHY_REPORT,
      params,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  Ответ МЧС (structure-ttp, остался только этот тип после модернизации)
   *
   * @param params
   * @param data
   * @returns
   */
  async generatePhysicalPersonAnswerPnp(
    params: AnswerPnpReportForm,
    data: QuasarTableExportColumns
  ): Promise<any> {
    const response = await http({
      ...TTP.STRUCTURE_TTP,
      params: { ...params, ...data },
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  Реестр ТХД (structure-thd)
   *
   * @param params
   * @param data
   * @returns
   */
  async generatePhysicalPersonAnswerThd(
    params: AnswerPnpReportForm,
    data: QuasarTableExportColumns
  ): Promise<any> {
    const response = await http({
      ...TTP.STRUCTURE_THD,
      params: { ...params, ...data },
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчтеа:
   *  Реестры ТТП(краткий/полный)
   * @param params
   * @returns
   */
  async generateRegistryReport(params: RegistryReportForm): Promise<any> {
    const response = await http({
      ...TTP.REGISTRY_TTP,
      params,
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  "Расширенный отчет по организациям, включающий в себя информацию по учредителям, а также численности работников"
   * @returns
   */
  async generateEmployeesOfOrganizationsReport(): Promise<any> {
    const response = await http({
      ...TTP.EMPLOYEES_TTP,
      responseType: 'blob',
    });

    return response;
  }

  /**
   * Генерация отчета:
   *  "Реализация отчета об организации"
   * @param {OrganizationsReportForm} params
   * @returns
   */
  async generateOrganizationsReport(params: OrganizationsReportForm): Promise<any> {
    const response = await http({
      ...TTP.ORGANIZATIONS,
      responseType: 'blob',
      params,
    });

    return response;
  }

  async generateDsznExport(params) {
    const response = await http({
      ...TTP.DSZN_XML_EXPORT,
      params,
    });

    return response;
  }

  async issueTmcReport(params: Partial<TTPRegistries.IssuedTmcReportParams>) {
    const endpoint = TTP.ISSUED_TMC;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async issueTmcTaskReport(params: Partial<TTPRegistries.IssuedTmcReportParams>) {
    const endpoint = TTP.ISSUED_TMC_TASK;
    const response = await http({ ...endpoint, params });
    return response;
  }

  async issuedTmcByQuarterReport(params) {
    const endpoint = TTP.ISSUED_TMC_BY_QUARTER;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async deathCertificateReport(params) {
    const endpoint = TTP.DEATH_CERTIFICATE;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async familiesTtpReport(params) {
    const endpoint = TTP.FAMILIES_TTP;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async birthCertificateReport(params) {
    const endpoint = TTP.BIRTH_CERTIFICATE;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async acceptedTmcReport(params) {
    const endpoint = TTP.ACCEPTED_TMC;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async mspKmnsReport(params) {
    const endpoint = TTP.MSP_KMNS;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async registerIssuingCertificatesReport(params) {
    const endpoint = TTP.REGISTER_ISSUING_CERTIFICATES;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async acceptedIssuesReport(params) {
    const endpoint = TTP.ACCEPTED_ISSUES;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async issueRegisterReport(params) {
    const endpoint = TTP.ISSUE_REGISTER;
    const response = await http({ ...endpoint, params, responseType: 'blob' });
    return response;
  }

  async registerIssuingReport() {
    const endpoint = TTP.REGISTER_ISSUING;
    const response = await http({ ...endpoint, responseType: 'blob' });
    return response;
  }

  /**
   * Генерация отчета:
   *  Реестр ТОЖ — проверка данных ФЛ (только ЯНАО).
   *  ФЛ с отсутствующими/невалидными СНИЛС/ИНН, серией/номером ДУЛ или датой/номером акта кочевья.
   */
  async tojDataQualityReport(params: { filename?: string }) {
    const response = (await http({
      ...TTP.TOJ_DATA_QUALITY,
      params,
    })) as AxiosResponse<ReportTaskBare>;

    return response;
  }

  /**
   * Генерация отчета:
   *  Информация об обучении учащихся школ (только ЯНАО).
   */
  async educationSchReport(params: {
    municipalityIds?: number[];
    filename?: string;
    title?: string;
  }) {
    const response = (await http({
      ...TTP.EDUCATION_SCH,
      params,
    })) as AxiosResponse<ReportTaskBare>;

    return response;
  }

  /**
   * Генерация отчета:
   *  Информация об обучении учащихся СПО (только ЯНАО).
   */
  async educationSpoReport(params: {
    municipalityIds?: number[];
    filename?: string;
    title?: string;
  }) {
    const response = (await http({
      ...TTP.EDUCATION_SPO,
      params,
    })) as AxiosResponse<ReportTaskBare>;

    return response;
  }

  /**
   * Генерация отчета:
   *  Информация о выплатах ГСМ (только ЯНАО).
   */
  async paymentsGsmReport(params: {
    municipalityIds?: number[];
    settlementsIds?: number[];
    status?: number;
    orderBy?: 'person_name' | 'birth_date' | 'municipality' | 'settlement' | 'status';
    fromDate?: string;
    toDate?: string;
    filename?: string;
    title?: string;
  }) {
    const response = (await http({
      ...TTP.PAYMENTS_GSM,
      params,
    })) as AxiosResponse<ReportTaskBare>;

    return response;
  }
}
